import { db } from '../firebase';
import { collection, query, getDocs, where, Timestamp, doc, updateDoc, setDoc, getDoc, writeBatch, deleteDoc } from 'firebase/firestore';

// Type for user stats by date
export interface UserStatsByDate {
  date: string;
  count: number;
  free: number;
  paid: number;
  trial: number;
}

/**
 * Check if a user with the given email already exists in the database
 * @param email - Email address to check
 * @returns Boolean indicating if the email exists, and the userId if it does
 */
export const checkEmailExists = async (email: string): Promise<{exists: boolean, userId?: string}> => {
  try {
    // Create a query against the users collection
    const usersCollection = collection(db, 'users');
    const q = query(usersCollection, where('email', '==', email));
    
    // Execute the query
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Email exists, return the first user ID found
      const existingUserId = querySnapshot.docs[0].id;
      return { exists: true, userId: existingUserId };
    }
    
    // Email doesn't exist
    return { exists: false };
  } catch (error) {
    console.error('Error checking if email exists:', error);
    // Default to assuming email doesn't exist to avoid blocking legitimate signups
    return { exists: false };
  }
};

/**
 * Save a user's project description to Firebase
 * @param userId - The ID of the user
 * @param projectDescription - The project description to save
 */
export const saveUserProject = async (userId: string, projectDescription: string): Promise<void> => {
  try {
    // Reference to the user document
    const userDocRef = doc(db, 'users', userId);
    
    // Check if the document exists first
    const docSnap = await getDoc(userDocRef);
    
    if (docSnap.exists()) {
      // Update existing document
      await updateDoc(userDocRef, {
        projectDescription,
        projectUpdatedAt: Timestamp.now()
      });
    } else {
      // Create new document if it doesn't exist
      await setDoc(userDocRef, {
        projectDescription,
        projectUpdatedAt: Timestamp.now(),
        createdAt: Timestamp.now()
      });
    }
    
    console.log('Project description saved successfully');
  } catch (error) {
    console.error('Error saving project description:', error);
    throw error;
  }
};

/**
 * Get a user's project description from Firebase
 * @param userId - The ID of the user
 * @returns The project description or null if not found
 */
export const getUserProject = async (userId: string): Promise<string | null> => {
  try {
    // Reference to the user document
    const userDocRef = doc(db, 'users', userId);
    
    // Get the user document
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists() && userDoc.data().projectDescription) {
      return userDoc.data().projectDescription;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting project description:', error);
    return null;
  }
};

/**
 * Get new users registered per day
 * Returns data for the last 30 days
 */
export const getNewUsersByDay = async (): Promise<UserStatsByDate[]> => {
  try {
    // Calculate date 30 days ago
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    // Query users created in the last 30 days
    const usersCollection = collection(db, 'users');
    const userQuery = query(
      usersCollection,
      where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo))
    );
    
    const usersSnapshot = await getDocs(userQuery);
    
    // Map to store counts by date
    const usersByDay = new Map<string, { count: number, free: number, paid: number, trial: number }>();
    
    // Initialize all days in the last 30 days with 0 counts
    for (let i = 0; i < 30; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      usersByDay.set(dateString, { count: 0, free: 0, paid: 0, trial: 0 });
    }
    
    // Count users by day and subscription status
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      if (userData.createdAt) {
        const createdAt = userData.createdAt.toDate();
        const dateString = createdAt.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        
        // Get current stats for this date or initialize with zeros
        const currentStats = usersByDay.get(dateString) || { count: 0, free: 0, paid: 0, trial: 0 };
        
        // Increment total count
        currentStats.count += 1;
        
        // Increment counts by subscription status
        if (userData.subscription?.status === 'active') {
          currentStats.paid += 1;
        } else if (userData.subscription?.status === 'trialing') {
          currentStats.trial += 1;
        } else {
          currentStats.free += 1;
        }
        
        // Update the map
        usersByDay.set(dateString, currentStats);
      }
    });
    
    // Convert map to array and sort by date
    const result = Array.from(usersByDay.entries())
      .map(([date, stats]) => ({ 
        date,
        ...stats
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
      
    return result;
  } catch (error) {
    console.error('Error fetching new users by day:', error);
    return [];
  }
};

/**
 * Merge two user accounts, transferring data from the source user to the target user
 * This should be used when the same email has multiple accounts
 * @param sourceUserId - The ID of the user to merge from
 * @param targetUserId - The ID of the user to merge to
 * @returns Success status
 */
export const mergeUserAccounts = async (sourceUserId: string, targetUserId: string): Promise<boolean> => {
  try {
    // References to both user documents
    const sourceUserRef = doc(db, 'users', sourceUserId);
    const targetUserRef = doc(db, 'users', targetUserId);
    
    // Get user data
    const sourceUserDoc = await getDoc(sourceUserRef);
    const targetUserDoc = await getDoc(targetUserRef);
    
    if (!sourceUserDoc.exists()) {
      console.error(`Source user ${sourceUserId} does not exist`);
      return false;
    }
    
    if (!targetUserDoc.exists()) {
      console.error(`Target user ${targetUserId} does not exist`);
      return false;
    }
    
    const sourceUserData = sourceUserDoc.data();
    const targetUserData = targetUserDoc.data();
    
    // Create a batch for atomic updates
    const batch = writeBatch(db);
    
    // Merge user data (target takes precedence for basic fields)
    const mergedData = {
      // Keep target's basic info
      uid: targetUserData.uid,
      email: targetUserData.email,
      displayName: targetUserData.displayName,
      photoURL: targetUserData.photoURL,
      
      // Keep the earliest creation date
      createdAt: sourceUserData.createdAt && targetUserData.createdAt
        ? (sourceUserData.createdAt.toMillis() < targetUserData.createdAt.toMillis() 
            ? sourceUserData.createdAt 
            : targetUserData.createdAt)
        : (sourceUserData.createdAt || targetUserData.createdAt),
      
      // Use the most recent login
      lastLogin: targetUserData.lastLogin || sourceUserData.lastLogin,
      
      // Merge custom data
      projectDescription: targetUserData.projectDescription || sourceUserData.projectDescription,
      
      // Keep tracking that this was a merged account
      mergedFrom: sourceUserId,
      mergedAt: Timestamp.now()
    };
    
    // Update the target user with merged data
    batch.update(targetUserRef, mergedData);
    
    // Mark the source user as merged
    batch.update(sourceUserRef, {
      mergedTo: targetUserId,
      mergedAt: Timestamp.now(),
      isMerged: true
    });
    
    // Find and merge other collections related to the user
    // For example, notes, playlists, etc.
    
    // Example: Merge notes
    const notesCollection = collection(db, 'notes');
    const sourceNotesQuery = query(notesCollection, where('userId', '==', sourceUserId));
    const sourceNotesSnapshot = await getDocs(sourceNotesQuery);
    
    sourceNotesSnapshot.forEach(noteDoc => {
      const noteData = noteDoc.data();
      // Create a new note document for the target user
      const newNoteRef = doc(collection(db, 'notes'));
      batch.set(newNoteRef, {
        ...noteData,
        userId: targetUserId,
        originalId: noteDoc.id,
        mergedFrom: sourceUserId,
        updatedAt: Timestamp.now()
      });
    });
    
    // Similar logic can be applied for other collections
    
    // Commit all changes
    await batch.commit();
    console.log(`Successfully merged user ${sourceUserId} into ${targetUserId}`);
    
    return true;
  } catch (error) {
    console.error('Error merging user accounts:', error);
    return false;
  }
};

/**
 * Find all users with duplicate email addresses
 * @returns Map of emails to arrays of user IDs
 */
export const findDuplicateUserEmails = async (): Promise<Map<string, string[]>> => {
  try {
    // Get all users
    const usersCollection = collection(db, 'users');
    const usersSnapshot = await getDocs(usersCollection);
    
    // Create a map to track emails and their associated user IDs
    const emailMap = new Map<string, string[]>();
    
    // Process all users
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      if (userData.email) {
        const email = userData.email.toLowerCase(); // Normalize email to lowercase
        
        // Get existing users with this email or create a new array
        const existingUsers = emailMap.get(email) || [];
        
        // Add this user ID to the array
        existingUsers.push(doc.id);
        
        // Update the map
        emailMap.set(email, existingUsers);
      }
    });
    
    // Filter to only include emails with multiple users
    const duplicatesMap = new Map<string, string[]>();
    emailMap.forEach((userIds, email) => {
      if (userIds.length > 1) {
        duplicatesMap.set(email, userIds);
      }
    });
    
    return duplicatesMap;
  } catch (error) {
    console.error('Error finding duplicate user emails:', error);
    return new Map();
  }
};

/**
 * Resolve all duplicate accounts by merging them
 * This will keep the most recently active account and merge data from others into it
 * @returns Object with count of resolved duplicates and any errors
 */
export const resolveDuplicateAccounts = async (): Promise<{resolved: number, errors: {email: string, error: string}[]}> => {
  try {
    const duplicates = await findDuplicateUserEmails();
    const results = {
      resolved: 0,
      errors: [] as {email: string, error: string}[]
    };
    
    // Process each set of duplicate accounts
    // Convert Map entries to array to avoid iteration issues
    const duplicateEntries = Array.from(duplicates.entries());
    
    for (const [email, userIds] of duplicateEntries) {
      try {
        console.log(`Resolving duplicates for email ${email}, found ${userIds.length} accounts`);
        
        if (userIds.length <= 1) {
          continue; // Skip if somehow there's only one user now
        }
        
        // Get details for all accounts to determine which to keep
        const userDocs = await Promise.all(
          userIds.map((userId: string) => getDoc(doc(db, 'users', userId)))
        );
        
        // Find the most recently active account to keep as the primary
        let primaryUserIndex = 0;
        let mostRecentLogin = new Date(0); // Start with epoch time
        
        userDocs.forEach((userDoc, index) => {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.lastLogin) {
              const loginTime = userData.lastLogin.toDate();
              if (loginTime > mostRecentLogin) {
                mostRecentLogin = loginTime;
                primaryUserIndex = index;
              }
            }
          }
        });
        
        const primaryUserId = userIds[primaryUserIndex];
        
        // Merge all other accounts into the primary
        for (let i = 0; i < userIds.length; i++) {
          if (i !== primaryUserIndex) {
            const sourceUserId = userIds[i];
            await mergeUserAccounts(sourceUserId, primaryUserId);
            results.resolved++;
          }
        }
      } catch (error) {
        console.error(`Error resolving duplicates for email ${email}:`, error);
        results.errors.push({
          email,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error resolving duplicate accounts:', error);
    return {
      resolved: 0,
      errors: [{
        email: 'global',
        error: error instanceof Error ? error.message : String(error)
      }]
    };
  }
};

/**
 * Find and fix duplicate accounts for a specific email address
 * This is a utility function to help users resolve their specific issue
 * @param email The email address to check for duplicates
 * @returns Result of the merge operation
 */
export const fixDuplicateAccountsForEmail = async (email: string): Promise<{
  duplicatesFound: number, 
  merged: boolean, 
  primaryAccount?: string,
  error?: string
}> => {
  try {
    // Normalize email
    const normalizedEmail = email.toLowerCase().trim();
    
    // Query for users with this email
    const usersCollection = collection(db, 'users');
    const q = query(usersCollection, where('email', '==', normalizedEmail));
    const querySnapshot = await getDocs(q);
    
    // Extract user IDs
    const userIds: string[] = [];
    querySnapshot.forEach(doc => {
      userIds.push(doc.id);
    });
    
    // Check if duplicates were found
    if (userIds.length <= 1) {
      return {
        duplicatesFound: userIds.length,
        merged: false,
        primaryAccount: userIds[0],
        error: userIds.length === 0 ? 'No accounts found with this email' : undefined
      };
    }
    
    // Find the most recently active account
    const userDocs = await Promise.all(
      userIds.map((userId: string) => getDoc(doc(db, 'users', userId)))
    );
    
    let primaryUserIndex = 0;
    let mostRecentLogin = new Date(0); // Start with epoch time
    
    userDocs.forEach((userDoc, index) => {
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.lastLogin) {
          const loginTime = userData.lastLogin.toDate();
          if (loginTime > mostRecentLogin) {
            mostRecentLogin = loginTime;
            primaryUserIndex = index;
          }
        }
      }
    });
    
    const primaryUserId = userIds[primaryUserIndex];
    
    // Merge all other accounts into the primary
    for (let i = 0; i < userIds.length; i++) {
      if (i !== primaryUserIndex) {
        const sourceUserId = userIds[i];
        await mergeUserAccounts(sourceUserId, primaryUserId);
      }
    }
    
    return {
      duplicatesFound: userIds.length,
      merged: true,
      primaryAccount: primaryUserId
    };
    
  } catch (error) {
    console.error('Error fixing duplicate accounts:', error);
    return {
      duplicatesFound: 0,
      merged: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
}; 